import { faBuilding, faSave, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import Editor from 'components/editor/Editor';
import TagSearch from 'components/tags/TagSearch';
import useApi from 'helpers/api';
import { processHtmlForEmail } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Badge, Col, Form, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import MaskedInput from 'react-text-mask';

const CustomerInfo = ({ data, error, success, loading }) => {
  const api = useApi();
  const form = useRef(null);
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [body, setBody] = useState({ tags: [] });
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    setBody({
      ...data,
      tags: data.tags ? data.tags.map(tag => tag.name) : [],
      observation: data.observation || ''
    });
  }, [data]);

  const handleAddTag = tag => {
    setBody(prevBody => ({
      ...prevBody,
      tags: [...prevBody.tags, tag]
    }));
  };

  const handleRemoveTag = tag => {
    setBody(prevBody => ({
      ...prevBody,
      tags: prevBody.tags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (form.current.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
    } else {
      const formattedBody = {
        ...body,
        cnpj: body.cnpj === '' ? null : body.cnpj,
        cpf: body.cpf === '' ? null : body.cpf,
        observation: processHtmlForEmail(body.observation) || null
      };
      const response = await api.customers.edit(body.id, formattedBody);

      if (response.status) {
        error(response);
        showErrorNotification(response.userMessage);
      } else {
        success(true);
        showSuccessNotification('Cliente editado com sucesso');
        setBody({
          ...response,
          tags: response.tags ? response.tags.map(tag => tag.name) : [],
          observation: response.observation || ''
        });
      }

      setValidated(false);
    }
  };

  const isJuridicalPerson = body.type === 'JURIDICAL_PERSON';

  return (
    <div className="mb-3">
      <div>
        <Row>
          <Col lg xxl={12}>
            <Row className="justify-content-between align-items-center">
              <Col>
                <h6 className="fw-semi-bold ls mb-3 text-uppercase">
                  Editar informações
                </h6>
              </Col>
              <Col xs="auto">
                <Form.Group>
                  <Flex alignItems="center" gap={2}>
                    <Form.Label className="m-0 p-0 me-2 fs-10">
                      Ativo{' '}
                    </Form.Label>
                    {loading ? (
                      <Skeleton width={30} height={20} baseColor="#5E6E82" />
                    ) : (
                      <Form.Check
                        className="fs-10"
                        type="switch"
                        name="inactive"
                        checked={!body.inactive}
                        onChange={() =>
                          setBody(prevBody => ({
                            ...prevBody,
                            inactive: !prevBody.inactive
                          }))
                        }
                      />
                    )}
                  </Flex>
                </Form.Group>
              </Col>
            </Row>

            <Form validated={validated} ref={form} onSubmit={handleSubmit}>
              {/* Primeira linha - Tipo */}
              <Row className="mb-3">
                <Form.Group as={Col} md="6" controlId="customer.type">
                  <Form.Label>Tipo</Form.Label>
                  {loading ? (
                    <Skeleton height={38} baseColor="#5E6E82" />
                  ) : (
                    <Form.Group>
                      <Form.Check
                        inline
                        type="radio"
                        label={
                          <>
                            <FontAwesomeIcon
                              icon={faBuilding}
                              className="me-1"
                            />
                            Pessoa Jurídica
                          </>
                        }
                        value="JURIDICAL_PERSON"
                        name="type"
                        checked={isJuridicalPerson}
                        onChange={e =>
                          setBody(prevBody => ({
                            ...prevBody,
                            type: e.target.value
                          }))
                        }
                      />
                      <Form.Check
                        type="radio"
                        inline
                        label={
                          <>
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            Pessoa Física
                          </>
                        }
                        value="NATURAL_PERSON"
                        name="type"
                        checked={!isJuridicalPerson}
                        onChange={e =>
                          setBody(prevBody => ({
                            ...prevBody,
                            type: e.target.value
                          }))
                        }
                      />
                    </Form.Group>
                  )}
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Insira um tipo.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} md="6" controlId="tags">
                  <Form.Label>Tags</Form.Label>
                  <TagSearch onAddTag={handleAddTag} />
                  <div className="mt-1">
                    {body.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        bg="light"
                        text="dark"
                        className="me-1 p-2"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRemoveTag(tag)}
                      >
                        {tag} &times;
                      </Badge>
                    ))}
                  </div>
                </Form.Group>
              </Row>

              {/* Segunda linha - Nome e Razão social */}
              <Row className="mb-3">
                {isJuridicalPerson ? (
                  <Form.Group
                    as={Col}
                    md="6"
                    controlId="customer.corporateName"
                  >
                    <Form.Label>Razão social</Form.Label>
                    {loading ? (
                      <Skeleton height={38} baseColor="#5E6E82" />
                    ) : (
                      <Form.Control
                        required
                        type="text"
                        placeholder="Razão social"
                        value={body.corporateName || ''}
                        onChange={e =>
                          setBody(prevBody => ({
                            ...prevBody,
                            corporateName: e.target.value
                          }))
                        }
                      />
                    )}
                    <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      Insira uma razão social.
                    </Form.Control.Feedback>
                  </Form.Group>
                ) : (
                  <Form.Group as={Col} md="6" controlId="customer.name">
                    <Form.Label>Nome completo</Form.Label>
                    {loading ? (
                      <Skeleton height={38} baseColor="#5E6E82" />
                    ) : (
                      <Form.Control
                        required
                        type="text"
                        placeholder="Nome completo"
                        value={body.name || ''}
                        onChange={e =>
                          setBody(prevBody => ({
                            ...prevBody,
                            name: e.target.value
                          }))
                        }
                      />
                    )}
                    <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      Insira um nome completo.
                    </Form.Control.Feedback>
                  </Form.Group>
                )}

                <Form.Group as={Col} md="6" controlId="customer.fantasyName">
                  <Form.Label>Nome fantasia</Form.Label>
                  {loading ? (
                    <Skeleton height={38} baseColor="#5E6E82" />
                  ) : (
                    <Form.Control
                      required
                      type="text"
                      placeholder="Nome fantasia"
                      value={body.fantasyName || ''}
                      onChange={e =>
                        setBody(prevBody => ({
                          ...prevBody,
                          fantasyName: e.target.value
                        }))
                      }
                    />
                  )}
                  <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    Insira um nome fantasia.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              {/* Segunda linha - Campos específicos para Pessoa Física */}
              {!isJuridicalPerson && (
                <>
                  <Row className="mb-3">
                    <Form.Group as={Col} md="4" controlId="customer.cpf">
                      <Form.Label>CPF</Form.Label>
                      {loading ? (
                        <Skeleton height={38} baseColor="#5E6E82" />
                      ) : (
                        <MaskedInput
                          mask={[
                            /[0-9]/,
                            /\d/,
                            /\d/,
                            '.',
                            /\d/,
                            /\d/,
                            /\d/,
                            '.',
                            /\d/,
                            /\d/,
                            /\d/,
                            '-',
                            /\d/,
                            /\d/
                          ]}
                          guide={false}
                          required
                          type="text"
                          placeholder="CPF"
                          value={body.cpf || ''}
                          onChange={e =>
                            setBody(prevBody => ({
                              ...prevBody,
                              cpf: e.target.value
                            }))
                          }
                          render={(ref, props) => (
                            <Form.Control ref={ref} {...props} />
                          )}
                        />
                      )}
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        Insira um CPF.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md="4" controlId="customer.birthday">
                      <Form.Label>Data de nascimento</Form.Label>
                      {loading ? (
                        <Skeleton height={38} baseColor="#5E6E82" />
                      ) : (
                        <Form.Control
                          type="date"
                          value={body.birthday || ''}
                          onChange={e =>
                            setBody(prevBody => ({
                              ...prevBody,
                              birthday: e.target.value
                            }))
                          }
                          max={new Date().toISOString().split('T')[0]}
                        />
                      )}
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md="4" controlId="customer.gender">
                      <Form.Label>Gênero</Form.Label>
                      {loading ? (
                        <Skeleton height={38} baseColor="#5E6E82" />
                      ) : (
                        <Form.Select
                          aria-label="Gênero"
                          value={body.gender || ''}
                          onChange={e =>
                            setBody(prevBody => ({
                              ...prevBody,
                              gender: e.target.value
                            }))
                          }
                        >
                          <option value="">Selecione uma opção</option>
                          <option value="MALE">Masculino</option>
                          <option value="FEMININE">Feminino</option>
                        </Form.Select>
                      )}
                      <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </>
              )}

              {/* Campos específicos para Pessoa Jurídica */}
              {isJuridicalPerson && (
                <Row className="mb-3">
                  <Form.Group as={Col} md="6" controlId="customer.cnpj">
                    <Form.Label>CNPJ</Form.Label>
                    {loading ? (
                      <Skeleton height={38} baseColor="#5E6E82" />
                    ) : (
                      <MaskedInput
                        mask={[
                          /[0-9]/,
                          /\d/,
                          '.',
                          /\d/,
                          /\d/,
                          /\d/,
                          '.',
                          /\d/,
                          /\d/,
                          /\d/,
                          '/',
                          /\d/,
                          /\d/,
                          /\d/,
                          /\d/,
                          '-',
                          /\d/,
                          /\d/
                        ]}
                        guide={false}
                        required
                        type="text"
                        placeholder="CNPJ"
                        value={body.cnpj || ''}
                        onChange={e =>
                          setBody(prevBody => ({
                            ...prevBody,
                            cnpj: e.target.value
                          }))
                        }
                        render={(ref, props) => (
                          <Form.Control ref={ref} {...props} />
                        )}
                      />
                    )}
                    <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      Insira um CNPJ.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group
                    as={Col}
                    md="6"
                    controlId="customer.stateRegistration"
                  >
                    <Form.Label>Inscrição estadual</Form.Label>
                    {loading ? (
                      <Skeleton height={38} baseColor="#5E6E82" />
                    ) : (
                      <Form.Control
                        type="text"
                        placeholder="Inscrição estadual"
                        value={body.stateRegistration || ''}
                        onChange={e =>
                          setBody(prevBody => ({
                            ...prevBody,
                            stateRegistration: e.target.value
                          }))
                        }
                      />
                    )}
                    <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      Insira uma inscrição estadual.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              )}

              {/* Campo de observações */}
              <Row className="mb-3">
                <Form.Group as={Col} md="12" controlId="customer.observation">
                  <Form.Label>Observação</Form.Label>
                  {loading ? (
                    <Skeleton height={150} baseColor="#5E6E82" />
                  ) : (
                    <Editor
                      data={body.observation || ''}
                      value={html =>
                        setBody(prevBody => ({
                          ...prevBody,
                          observation: html || ''
                        }))
                      }
                    />
                  )}
                </Form.Group>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>

      <Flex justifyContent={'end'} className="mt-4 pt-2">
        {loading ? (
          <Skeleton width={100} height={38} baseColor="#5E6E82" />
        ) : (
          <IconButton
            className="ms-2"
            iconClassName="fs--2 me-1"
            icon={faSave}
            onClick={handleSubmit}
          >
            Salvar
          </IconButton>
        )}
      </Flex>
    </div>
  );
};

export default CustomerInfo;
